import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showSuccess, showError } from "../../utils/snackbarHelpers";
import { config } from '../../config';

const AcceptInvitationRequest = () => {
    const { boardId, shortId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [accepting, setAccepting] = useState(false);

    // Extract token from query parameters
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');

    const invitationType = boardId ? 'board' : 'workspace';
    const id = boardId || shortId; // Either boardId or workspace shortId

    // Detect token type by decoding JWT payload (no verification, just base64)
    const getTokenType = (token) => {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            if ((payload.boardId || payload.workspaceId) && !payload.email) {
                return 'joinLink';
            }
            return 'emailInvitation';
        } catch {
            return 'emailInvitation'; // fallback to existing behavior
        }
    };

    useEffect(() => {
        if (!token) {
            showError(dispatch, 'Invalid link: Missing token');
            navigate('/');
            return;
        }

        const tokenType = getTokenType(token);

        const acceptInvitation = async () => {
            setAccepting(true);
            try {
                let endpoint;
                let body;

                if (tokenType === 'joinLink') {
                    // Route to the join-by-link endpoint (requires auth)
                    endpoint = invitationType === 'board'
                        ? `${config.API_URI}/v1/boards/join-board`
                        : `${config.API_URI}/v1/workspace/join-workspace`;
                    body = JSON.stringify({ token });
                } else {
                    // Email invitation — existing flow (public endpoint)
                    endpoint = invitationType === 'board'
                        ? `${config.API_URI}/v1/boards/accept`
                        : `${config.API_URI}/v1/workspace/accept`;
                    body = JSON.stringify({ id, token });
                }

                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body,
                });

                // If unauthorized (join link requires login), redirect to login
                if (response.status === 401 && tokenType === 'joinLink') {
                    const currentUrl = window.location.pathname + window.location.search;
                    sessionStorage.setItem('returnTo', currentUrl);
                    window.location.href = `/login?returnTo=${encodeURIComponent(currentUrl)}`;
                    return;
                }

                const data = await response.json();

                if (response.ok) {
                    // Save the access token if provided (for backward compatibility)
                    if (data.accessToken) {
                        localStorage.setItem('accessToken', data.accessToken);
                    }

                    // Clear returnTo since we've successfully joined
                    sessionStorage.removeItem('returnTo');

                    // Determine success message based on user status
                    let successMessage = `Successfully joined the ${invitationType}`;
                    if (data.userActivated) {
                        successMessage = `Welcome! Your account has been activated and you've joined the ${invitationType}`;
                    } else if (data.userVerified) {
                        successMessage = `Your account has been verified and you've joined the ${invitationType}`;
                    }

                    showSuccess(dispatch, successMessage);

                    setTimeout(() => {
                        if (!data.isDefaultPasswordSet) {
                            // Redirect to set password page
                            window.location.href = '/set-password';
                        } else {
                            // Redirect to the board or workspace
                            const redirectPath =
                                invitationType === 'board' ? `/b/${id}` : `/w/${id}/boards`;
                            window.location.href = redirectPath;
                        }
                    }, 1500);
                } else {
                    showError(dispatch, data.message || `Failed to join ${invitationType}`);
                }
            } catch (error) {
                showError(dispatch, `Error accepting ${invitationType}`);
            } finally {
                setAccepting(false);
            }
        };

        acceptInvitation();
    }, [token, id, invitationType, dispatch, navigate]);

    if (accepting) {
        return <div>Joining {invitationType}...</div>;
    }

    return null;
};

export default AcceptInvitationRequest;
